/**
 * Earnings Alpha — live earnings-event signal (hold through the print, 1 day).
 * Rule: consistent-tail-fund buy + <=3 total 13F buyers + high rv20 + cap >= $2B.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, RefreshCw } from "lucide-react";
import {
  CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip,
  XAxis, YAxis,
} from "recharts";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pct = (v, dp = 2) => (v == null ? "—" : `${v > 0 ? "+" : ""}${(v * 100).toFixed(dp)}%`);
const bp = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${Math.round(v * 1e4)}bp`);
const cls = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-rose-400" : "");

const StatCard = ({ label, value, sub, accent, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold font-mono ${accent || ""}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </CardContent>
  </Card>
);

const EquityChart = ({ curve }) => (
  <div className="h-64">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={curve} margin={{ top: 6, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8b8fa3" }}
               tickFormatter={(d) => d?.slice(2, 7)} minTickGap={48} />
        <YAxis tick={{ fontSize: 10, fill: "#8b8fa3" }} domain={["auto", "auto"]}
               scale="log" tickFormatter={(v) => `${v.toFixed(0)}`} width={46} />
        <Tooltip contentStyle={{ background: "#12141c", border: "1px solid rgba(255,255,255,0.1)",
                                 borderRadius: 8, fontSize: 12 }}
                 formatter={(v, name) => [v.toFixed(1), name === "hdg" ? "Hedged (net)" : "Raw (net)"]} />
        <ReferenceLine y={100} stroke="rgba(255,255,255,0.15)" strokeDasharray="3 3" />
        <Line type="monotone" dataKey="raw" stroke="#7c8494" strokeWidth={1.5}
              strokeDasharray="5 3" dot={false} />
        <Line type="monotone" dataKey="hdg" stroke="#34d399" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const TimingBadge = ({ t }) => (
  <Badge variant="outline" className={`text-[10px] ${
    t === "bmo" ? "text-sky-300 border-sky-500/40" : "text-amber-300 border-amber-500/40"}`}>
    {t?.toUpperCase()}
  </Badge>
);

const FundChips = ({ funds }) => (
  <span className="inline-flex flex-wrap gap-1">
    {(funds || []).map((f) => (
      <span key={f.cik || f} title={f.buy_date ? `bought ${f.buy_date}` : ""}
            className="border border-indigo-500/40 text-indigo-300 rounded px-1.5 py-0.5 text-[10px]">
        {(f.name || f).slice(0, 26)}
      </span>
    ))}
  </span>
);

const TiltBadge = ({ r }) => {
  if (r.runup_ex === null || r.runup_ex === undefined)
    return <span className="text-muted-foreground">—</span>;
  const up = r.runup_ex > 0;
  return (
    <span className="inline-flex items-center gap-1.5"
          title="Excess return vs SPY since the first conviction filing — sizes the trade 1.5× (outperformer) or 0.5×">
      <span className={`font-mono ${up ? "text-emerald-400" : "text-red-400"}`}>
        {r.runup_ex > 0 ? "+" : ""}{(r.runup_ex * 100).toFixed(1)}%
      </span>
      <span className={`rounded px-1 py-0.5 text-[10px] font-bold border ${
        up ? "bg-amber-500/15 text-amber-300 border-amber-500/50"
           : "bg-slate-500/15 text-slate-400 border-slate-500/40"}`}>
        {(r.weight_mult ?? 1).toFixed(1)}×
      </span>
    </span>
  );
};

const V3Badge = ({ r }) => {
  if (r.red) {
    return (
      <span className="inline-flex items-center"
            title={`RED veto (${r.red_count}/5 negative-drift flags): ${(r.red_flags || []).join(", ")}. Red 3+ earnings events averaged −43bp day-alpha OOS vs +73bp for flag-free names (Study E Add.25). Stand aside even if otherwise qualified.`}>
        <span className="rounded px-1.5 py-0.5 text-[10px] font-bold border bg-red-500/20 text-red-300 border-red-500/60">
          RED
        </span>
      </span>
    );
  }
  if (r.v3_band === null || r.v3_band === undefined)
    return <span className="text-muted-foreground" title="No Entry Anchoring cohort (no new 13F buys in the last two quarters)">—</span>;
  const green = r.v3_green;
  return (
    <span className="inline-flex items-center gap-1.5"
          title={`Entry Anchoring V3 score decile S${r.v3_band} (${r.v3_quarter}). GREEN = S6-9: validated EA sweet spot — hit 58.5% / +235bp vs 53.9% / +135bp all trades (OOS 57.1% / +226bp). S1-3 ≈ coin flip; S10 unstable.${r.red_count ? ` Red flags ${r.red_count}/5: ${(r.red_flags || []).join(", ")}.` : ""}`}>
      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold border ${
        green ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/60"
              : "bg-slate-500/10 text-slate-400 border-slate-500/40"}`}>
        {green ? "GREEN" : `S${r.v3_band}`}
      </span>
      {r.red_count >= 2 && (
        <span className="text-[10px] text-amber-400" title={`${r.red_count}/5 red flags: ${(r.red_flags || []).join(", ")}`}>⚑{r.red_count}</span>
      )}
    </span>
  );
};

const SignalsTable = ({ rows }) => (
  <table className="w-full text-xs" data-testid="ea-signals-table">
    <thead className="border-b border-border text-muted-foreground">
      <tr>
        <th className="py-1.5 px-2 text-left">Symbol</th>
        <th className="py-1.5 px-2 text-left">Earnings</th>
        <th className="py-1.5 px-2 text-left">Timing</th>
        <th className="py-1.5 px-2 text-left">Enter → Exit</th>
        <th className="py-1.5 px-2 text-left">Consistent funds</th>
        <th className="py-1.5 px-2 text-right">Buyers</th>
        <th className="py-1.5 px-2 text-right">rv20</th>
        <th className="py-1.5 px-2 text-right">Cap</th>
        <th className="py-1.5 px-2 text-right">Run-up · tilt</th>
        <th className="py-1.5 px-2 text-center" title="Entry Anchoring V3 band — S6-9 = green light">V3</th>
        <th className="py-1.5 px-2 text-left">Status</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r._id} className={`border-b border-border/40 ${!r.qualified ? "opacity-50" : ""}`}
            data-testid={`ea-signal-${r.symbol}`}>
          <td className="py-1.5 px-2 font-bold">{r.symbol}</td>
          <td className="py-1.5 px-2 font-mono">{r.earnings_date}</td>
          <td className="py-1.5 px-2"><TimingBadge t={r.timing} /></td>
          <td className="py-1.5 px-2 font-mono text-muted-foreground">{r.entry_date} → {r.exit_date}</td>
          <td className="py-1.5 px-2"><FundChips funds={r.funds} /></td>
          <td className="py-1.5 px-2 text-right font-mono">{r.n_all}</td>
          <td className="py-1.5 px-2 text-right font-mono">{r.rv20 ?? "—"}</td>
          <td className="py-1.5 px-2 text-right font-mono">{r.cap_b ? `$${r.cap_b}B` : "—"}</td>
          <td className="py-1.5 px-2 text-right" data-testid={`ea-tilt-${r.symbol}`}><TiltBadge r={r} /></td>
          <td className="py-1.5 px-2 text-center" data-testid={`ea-v3-${r.symbol}`}><V3Badge r={r} /></td>
          <td className="py-1.5 px-2">
            {r.qualified ? (
              <Badge variant="outline" className={`text-[10px] ${
                r.status === "armed" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/50"
                                     : "text-emerald-300 border-emerald-500/40"}`}>
                {r.status === "armed" ? "ARMED" : "QUALIFIED"}
              </Badge>
            ) : (
              <span className="text-[10px] text-muted-foreground">{r.reasons?.join(" · ")}</span>
            )}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CompletedTable = ({ rows }) => (
  <table className="w-full text-xs" data-testid="ea-completed-table">
    <thead className="border-b border-border text-muted-foreground">
      <tr>
        <th className="py-1.5 px-2 text-left">Symbol</th>
        <th className="py-1.5 px-2 text-left">Earnings</th>
        <th className="py-1.5 px-2 text-left">Funds</th>
        <th className="py-1.5 px-2 text-right">Raw net</th>
        <th className="py-1.5 px-2 text-right">Hedged net</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r._id} className="border-b border-border/40" data-testid={`ea-completed-${r.symbol}`}>
          <td className="py-1.5 px-2 font-bold">{r.symbol}</td>
          <td className="py-1.5 px-2 font-mono">{r.earnings_date}</td>
          <td className="py-1.5 px-2"><FundChips funds={r.funds} /></td>
          <td className={`py-1.5 px-2 text-right font-mono ${cls(r.realized_net_raw)}`}>{bp(r.realized_net_raw)}</td>
          <td className={`py-1.5 px-2 text-right font-mono ${cls(r.realized_net_hdg)}`}>{bp(r.realized_net_hdg)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const QuarterlyTable = ({ rows, selected, onSelect }) => (
  <table className="w-full text-xs" data-testid="ea-quarterly-table">
    <thead className="border-b border-border text-muted-foreground">
      <tr>
        <th className="py-1.5 px-2 text-left">Quarter</th>
        <th className="py-1.5 px-2 text-right">Trades</th>
        <th className="py-1.5 px-2 text-right">Raw net /trade</th>
        <th className="py-1.5 px-2 text-right">Hedged net /trade</th>
        <th className="py-1.5 px-2 text-right">Hit</th>
      </tr>
    </thead>
    <tbody>
      {[...rows].reverse().map((r) => (
        <tr key={r.cq} onClick={() => onSelect(r.cq)} data-testid={`ea-quarter-row-${r.cq}`}
            className={`border-b border-border/40 cursor-pointer transition-colors hover:bg-white/5 ${
              selected === r.cq ? "bg-emerald-500/10" : ""}`}>
          <td className="py-1.5 px-2 font-mono text-emerald-300 underline decoration-dotted underline-offset-2">{r.cq}</td>
          <td className="py-1.5 px-2 text-right font-mono">{r.n}</td>
          <td className={`py-1.5 px-2 text-right font-mono ${cls(r.raw_bp)}`}>{r.raw_bp > 0 ? "+" : ""}{r.raw_bp}bp</td>
          <td className={`py-1.5 px-2 text-right font-mono ${cls(r.hdg_bp)}`}>{r.hdg_bp > 0 ? "+" : ""}{r.hdg_bp}bp</td>
          <td className="py-1.5 px-2 text-right font-mono">{(r.hit * 100).toFixed(1)}%</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const TradeLogTable = ({ rows }) => (
  <table className="w-full text-xs" data-testid="ea-tradelog-table">
    <thead className="border-b border-border text-muted-foreground sticky top-0 bg-card">
      <tr>
        <th className="py-1.5 px-2 text-left">Print day</th>
        <th className="py-1.5 px-2 text-left">Ticker</th>
        <th className="py-1.5 px-2 text-left">Timing</th>
        <th className="py-1.5 px-2 text-left">Consistent funds</th>
        <th className="py-1.5 px-2 text-right">Buyers</th>
        <th className="py-1.5 px-2 text-right">Cap</th>
        <th className="py-1.5 px-2 text-right">rv20</th>
        <th className="py-1.5 px-2 text-right">Raw net</th>
        <th className="py-1.5 px-2 text-right">Hedged net</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((t) => (
        <tr key={`${t.symbol}${t.rx_day}`} className="border-b border-border/40"
            data-testid={`ea-trade-${t.symbol}-${t.rx_day}`}>
          <td className="py-1.5 px-2 font-mono text-muted-foreground">{t.rx_day}</td>
          <td className="py-1.5 px-2 font-bold">{t.symbol}</td>
          <td className="py-1.5 px-2"><TimingBadge t={t.timing} /></td>
          <td className="py-1.5 px-2"><FundChips funds={t.funds} /></td>
          <td className="py-1.5 px-2 text-right font-mono">{t.n_all}</td>
          <td className="py-1.5 px-2 text-right font-mono">{t.cap_b ? `$${t.cap_b}B` : "—"}</td>
          <td className="py-1.5 px-2 text-right font-mono">{t.rv20 ?? "—"}</td>
          <td className={`py-1.5 px-2 text-right font-mono ${cls(t.net_raw)}`}>{pct(t.net_raw)}</td>
          <td className={`py-1.5 px-2 text-right font-mono ${cls(t.net_hdg)}`}>{pct(t.net_hdg)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const EDGE = [
  ["Who — a cohort, not star funds", "A quarterly re-ranked cohort of ~130 funds whose buys repeatedly land in the top decile of earnings-day reactions (volatile-growth specialists: Whale Rock / Herald / Brown Capital types). The information is in cohort membership: the identical rule on non-cohort buys earns +55bp vs +195bp (diff t=3.0) — but ranking funds inside the cohort has zero persistence (placebo-calibrated), so no fund is weighted above another."],
  ["Where — only when nobody else is in", "Their buys only pay in under-crowded names: ≤5 total 13F buyers in the prior 200 days. The same funds' buys in crowded names (≥10 buyers) carry zero edge — once the accumulation is widely shared, it is priced."],
  ["What kind — loud, liquid names", "20-day realized vol in the top third and market cap ≥ $2B. The intuitive filters failed testing: quiet-tape setups are anti-predictive (t=−2.3) and sub-$2B picks lose money (−157bp). The edge lives in volatile mid/large caps, median cap ~$4B."],
  ["When — the print is the whole trade", "All the alpha is the reaction day itself: +228bp/day on day 1, collapsing to +11bp/day by day 21. Days 2–21 add −5bp (t≈0) — no pre-drift worth front-running, no post-earnings drift worth riding. One day in, one day out, capital recycles to the next event."],
  ["Shape — a right-tail harvest, not a hit-rate game", "54.9% hit, but avg win +920bp vs avg loss −837bp; 33% of trades gain >5%. The market prices the direction of the print efficiently — what it misprices is the size of the reaction when a specialist cohort has quietly accumulated. Expect losing streaks; the edge is the asymmetry, proven over 2,740 trades (t=5.3, 16/21 quarters, 2021–2026)."],
];

const METHOD = [
  ["1 · Consistent-fund set (quarterly re-rank)", "Funds whose buys land in the top decile of subsector earnings-day alpha significantly more often than chance over the trailing 8 quarters (binomial z ≥ 1.5, ≥12 fund-events). The two-year memory resists single-regime hijacking. Formed strictly ex-ante."],
  ["2 · Anti-crowding filter", "The fund's buy (≥$1M, filed 1–200 days before the print) must be in a name with ≤5 total 13F buyers over the same window — crowded names (≥10 buyers) show zero edge."],
  ["3 · Volatility & size", "20-day realized vol above the trailing-4q 2/3 quantile (the edge lives in the loud names, not the quiet ones) and market cap ≥ $2B (sub-$2B picks are net losers)."],
  ["4 · Execution", "Enter at the close before the print reaction session, exit at the next close (1-day hold through the print). Horizon-validated: day 1 carries +228bp/day; every extension through day 21 adds nothing (days 2–21: −5bp, t≈0). BMO: enter D-1 close, exit D close. AMC: enter D close, exit D+1 close. Costs: 20bp raw / 30bp hedged round trip."],
];

export default function EarningsAlphaPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selQ, setSelQ] = useState(null);
  const [trades, setTrades] = useState(null);

  const loadTrades = useCallback(async (q) => {
    setSelQ(q);
    setTrades(null);
    try {
      const res = await fetch(`${API}/earnings-alpha/trades?quarter=${q}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTrades((await res.json()).trades);
    } catch (e) { toast.error(String(e.message || e)); setSelQ(null); }
  }, [token]);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/earnings-alpha/summary`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 404) { setError("not_initialized"); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
      setError(null);
    } catch (e) { setError(String(e.message || e)); }
  }, [token]);

  useEffect(() => { if (token) load(); }, [token, load]);

  const init = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/earnings-alpha/init`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "init failed");
      toast.success(body.note || "Initializing…");
      setTimeout(load, 45000);
      setTimeout(load, 90000);
    } catch (e) { toast.error(String(e.message || e)); } finally { setBusy(false); }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/earnings-alpha/refresh`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` } });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "refresh failed");
      toast.success(`Signals refreshed — ${body.signals} signals, ${body.settled} settled`);
      await load();
    } catch (e) { toast.error(String(e.message || e)); } finally { setBusy(false); }
  };

  const st = data?.meta?.stats;
  const pt = st?.per_trade?.hdg_net;
  const bk = st?.book?.hdg;
  const signals = data?.signals || [];
  const qualified = signals.filter((s) => s.qualified);
  const watch = signals.filter((s) => !s.qualified);

  return (
    <div className="p-6 space-y-6 max-w-[1500px]" data-testid="earnings-alpha-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarClock className="w-6 h-6 text-emerald-400" /> Earnings Alpha
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Hold-through-the-print event signal: buys by a quarterly re-ranked cohort of
            specialist 13F funds in under-crowded (≤5 buyers), volatile, ≥$2B names.
            One day in, one day out — all the alpha is the print reaction itself.
            Validated out-of-sample{st ? ` ${st.oos_start} → ${st.oos_end}` : ""} — the edge
            is the asymmetric right tail, not the hit rate.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={busy || !data}
                  data-testid="ea-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh signals
          </Button>
          <Button size="sm" onClick={init} disabled={busy} data-testid="ea-init-btn">
            {data ? "Rebuild" : "Initialize"}
          </Button>
        </div>
      </div>

      {error === "not_initialized" && (
        <Card data-testid="ea-init-card">
          <CardContent className="p-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              The Earnings Alpha engine has not been initialized yet.
            </p>
            <Button onClick={init} disabled={busy} data-testid="ea-init-btn-2">
              {busy ? "Initializing…" : "Initialize engine"}
            </Button>
          </CardContent>
        </Card>
      )}
      {error && error !== "not_initialized" && <p className="text-sm text-rose-400">{error}</p>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="ea-stats">
            <StatCard label="Per trade (hedged net)" value={pt ? `+${pt.mean_bp}bp` : "—"}
                      accent="text-emerald-400" sub={`t = ${pt?.t}`} testId="ea-stat-mean" />
            <StatCard label="Hit rate" value={pt ? `${(pt.hit * 100).toFixed(1)}%` : "—"}
                      sub="right-tail payoff" testId="ea-stat-hit" />
            <StatCard label="Trades" value={st?.n_trades ?? "—"}
                      sub={`~${Math.round((st?.n_trades || 0) / (st?.n_quarters || 1))}/quarter`} testId="ea-stat-n" />
            <StatCard label="Book ann. (hedged)" value={bk ? pct(bk.ann, 0) : "—"}
                      accent={cls(bk?.ann)} sub="5%/trade sizing" testId="ea-stat-ann" />
            <StatCard label="Sharpe" value={bk?.sharpe ?? "—"} testId="ea-stat-sharpe" />
            <StatCard label="Max drawdown" value={bk ? pct(bk.maxdd, 1) : "—"}
                      accent="text-rose-400" testId="ea-stat-dd" />
            <StatCard label="Consistent funds" value={data.meta?.fund_set?.length ?? "—"}
                      sub={`window ${data.meta?.fund_window?.[0]} → ${data.meta?.fund_window?.slice(-1)[0]} (8q)`} testId="ea-stat-funds" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
            <Card className="xl:col-span-3" data-testid="ea-equity-card">
              <CardHeader className="pb-1">
                <CardTitle className="text-sm">
                  OOS equity curve <span className="text-muted-foreground font-normal">
                    (index = 100, log scale, net of costs — solid: hedged, dashed: raw)</span>
                </CardTitle>
              </CardHeader>
              <CardContent><EquityChart curve={st?.curve || []} /></CardContent>
            </Card>
            <Card className="xl:col-span-2" data-testid="ea-quarterly-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  Quarterly backtest <span className="text-muted-foreground font-normal">
                    (quarter = calendar quarter of the print date — click a row for the trade log)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-72 overflow-y-auto">
                <QuarterlyTable rows={st?.quarterly || []} selected={selQ} onSelect={loadTrades} />
              </CardContent>
            </Card>
          </div>

          {selQ && (
            <Card data-testid="ea-tradelog-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span>
                    Trade log — {selQ} <span className="text-muted-foreground font-normal">
                      ({trades ? trades.length : "…"} trades, chronological; returns are net of costs,
                      close-before-print → close-after-print)</span>
                  </span>
                  <button onClick={() => { setSelQ(null); setTrades(null); }}
                          className="text-xs text-muted-foreground hover:text-foreground"
                          data-testid="ea-tradelog-close">✕ close</button>
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto max-h-[460px] overflow-y-auto">
                {trades ? <TradeLogTable rows={trades} />
                        : <p className="text-xs text-muted-foreground">Loading…</p>}
              </CardContent>
            </Card>
          )}

          <Card data-testid="ea-signals-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Upcoming signals <span className="text-muted-foreground font-normal">
                  ({qualified.length} qualified{watch.length ? `, ${watch.length} near-miss` : ""})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto max-h-[480px] overflow-y-auto">
              {signals.length ? <SignalsTable rows={[...qualified, ...watch]} />
                : <p className="text-xs text-muted-foreground py-4">
                    No upcoming earnings in the next 30 days match the candidate set — hit
                    Refresh signals around earnings season.</p>}
            </CardContent>
          </Card>

          {(data.completed || []).length > 0 && (
            <Card data-testid="ea-completed-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Settled live trades</CardTitle>
              </CardHeader>
              <CardContent className="max-h-80 overflow-y-auto">
                <CompletedTable rows={data.completed} />
              </CardContent>
            </Card>
          )}

          <Card data-testid="ea-edge-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                The edge, explained <span className="text-muted-foreground font-normal">
                  (what we actually found — and what we ruled out)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-4">
              {EDGE.map(([t, b], i) => (
                <div key={t} data-testid={`ea-edge-${i}`} className={i === 4 ? "md:col-span-2 xl:col-span-1" : ""}>
                  <div className="text-xs font-semibold text-emerald-300 mb-1">{t}</div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{b}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <Card data-testid="ea-method-card">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Methodology</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {METHOD.map(([t, b]) => (
                  <div key={t}>
                    <div className="text-xs font-semibold text-emerald-300 mb-1">{t}</div>
                    <p className="text-xs text-muted-foreground">{b}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card data-testid="ea-fundset-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  Current consistent-fund set <span className="text-muted-foreground font-normal">
                    (trailing 8 quarters, re-ranked quarterly)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-80 overflow-y-auto">
                <table className="w-full text-xs" data-testid="ea-fundset-table">
                  <thead className="border-b border-border text-muted-foreground">
                    <tr>
                      <th className="py-1.5 px-2 text-left">Fund</th>
                      <th className="py-1.5 px-2 text-right">z</th>
                      <th className="py-1.5 px-2 text-right" title="All covered buy-events, full history">Events</th>
                      <th className="py-1.5 px-2 text-right" title="Share of all their buy-events with positive earnings-day alpha">Hit</th>
                      <th className="py-1.5 px-2 text-right" title="Avg earnings-day alpha across all their buy-events">Avg</th>
                      <th className="py-1.5 px-2 text-right" title="Their trades inside the qualified rule (backtest)">In-rule</th>
                      <th className="py-1.5 px-2 text-right" title="Hit rate / avg net alpha inside the qualified rule — small samples, read with caution">Rule hit / avg</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.meta?.fund_set || []).map((f) => (
                      <tr key={f.cik} className="border-b border-border/40">
                        <td className="py-1.5 px-2">{f.name}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-emerald-300">{f.z}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{f.ev_all ?? f.n}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{f.hit_all != null ? `${(f.hit_all * 100).toFixed(0)}%` : "—"}</td>
                        <td className={`py-1.5 px-2 text-right font-mono ${cls(f.avg_bp)}`}>{f.avg_bp != null ? `${f.avg_bp > 0 ? "+" : ""}${f.avg_bp}bp` : "—"}</td>
                        <td className="py-1.5 px-2 text-right font-mono">{f.rule_trades ?? "—"}</td>
                        <td className={`py-1.5 px-2 text-right font-mono ${cls(f.rule_bp)}`}>
                          {f.rule_trades ? `${(f.rule_hit * 100).toFixed(0)}% / ${f.rule_bp > 0 ? "+" : ""}${f.rule_bp}bp` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-muted-foreground mt-2">
                  In-rule per-fund samples are small (1–31 trades) — judge funds on the
                  aggregate rule stats, not individual rows.
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
